import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'

import { CookieKeys } from '@/configs/cookies'
import { decodeJwtPayload } from '@/lib/auth/jwt'

const AUTH_COOKIE_OPTIONS = {
  path: '/',
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production'
}

export interface JWTPayload {
  exp: number
  iat: number
  roles?: string[]
  [key: string]: any
}

export const AUTH_TOKEN_KEY = CookieKeys.ACCESS_TOKEN
export const REFRESH_TOKEN_KEY = CookieKeys.REFRESH_TOKEN

export const setAuthToken = (token: string) => {
  try {
    const decoded = jwtDecode<JWTPayload>(token)
    const expirationDate = new Date(decoded.exp * 1000)

    Cookies.set(AUTH_TOKEN_KEY, token, {
      ...AUTH_COOKIE_OPTIONS,
      expires: expirationDate
    })

    return true
  } catch (error) {
    console.log('Error setting auth token:', error)
    return false
  }
}

export const setRefreshToken = (token: string) => {
  try {
    const decoded = jwtDecode<JWTPayload>(token)
    const expirationDate = new Date(decoded.exp * 1000)

    Cookies.set(REFRESH_TOKEN_KEY, token, {
      ...AUTH_COOKIE_OPTIONS,
      expires: expirationDate
    })

    return true
  } catch (error) {
    console.log('Error setting refresh token:', error)
    return false
  }
}

export const getAuthToken = (): string | undefined => {
  return Cookies.get(AUTH_TOKEN_KEY)
}

const decodeTokenPayload = (token?: string): JWTPayload | null =>
  decodeJwtPayload(token)

export const getAuthTokenPayload = (): JWTPayload | null => {
  return decodeTokenPayload(getAuthToken())
}

export const getRefreshToken = (): string | undefined => {
  return Cookies.get(REFRESH_TOKEN_KEY)
}

export const getRefreshTokenPayload = (): JWTPayload | null => {
  return decodeTokenPayload(getRefreshToken())
}

export const getSessionTokenPayload = (): JWTPayload | null => {
  return getAuthTokenPayload() ?? getRefreshTokenPayload()
}

export const removeAuthTokens = () => {
  Cookies.remove(AUTH_TOKEN_KEY, { path: '/' })
  Cookies.remove(REFRESH_TOKEN_KEY, { path: '/' })
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JWTPayload>(token)
    const currentTime = Date.now() / 1000
    return decoded.exp < currentTime
  } catch {
    return true
  }
}

export const getTokenExpiration = (token: string): Date | null => {
  try {
    const decoded = jwtDecode<JWTPayload>(token)
    return new Date(decoded.exp * 1000)
  } catch {
    return null
  }
}

export const isAuthenticated = (): boolean => {
  const refreshToken = getRefreshToken()
  if (!refreshToken || isTokenExpired(refreshToken)) {
    return false
  }

  return true
}

export { isJwtPayloadValid } from '@/lib/auth/jwt'
